import { useEffect } from 'react';

import { matchPath, useHistory, useLocation, useParams } from 'react-router';
import { generatePath } from 'react-router-dom';

import {
  overviewPagePath,
  teamChainsPagePath,
  teamModelApisPagePath,
  teamModelsPagePath,
  teamSettingsPagePath,
  teamTrainingPagePath,
} from '@/routes/constants';
import { useRecoilState } from 'recoil';

import { useTeamForSidebarQuery, useTeamsForSidebarQuery } from './__generated__/queries.generated';
import { selectedTeamIdState } from './atoms';
import { MatchParams, WorkspaceSections } from './types';

function useWorkspaceSection() {
  const location = useLocation();

  const match = (path: string) => matchPath(location.pathname, { path, exact: true });

  switch (true) {
    case !!match(teamModelApisPagePath):
      return WorkspaceSections.ModelAPIs;
    case !!match(teamModelsPagePath):
      return WorkspaceSections.Models;
    case !!match(teamChainsPagePath):
      return WorkspaceSections.Chains;
    case !!match(teamTrainingPagePath):
      return WorkspaceSections.Training;
    case !!match(overviewPagePath):
      return WorkspaceSections.Overview;
    default:
      return null;
  }
}

function useTeam(id: string) {
  const { data, loading } = useTeamForSidebarQuery({
    variables: { id },
    fetchPolicy: 'cache-first',
    skip: !id,
  });

  return { team: data?.team, loading };
}

function useTeams() {
  const { data, loading } = useTeamsForSidebarQuery({
    fetchPolicy: 'cache-first',
  });

  return { teams: data?.teams, loading };
}

function useSelectedTeamId() {
  const [selectedTeamId, setSelectedTeamId] = useRecoilState(selectedTeamIdState);
  const { teamId: teamIdFromUrl } = useParams<MatchParams>();
  const location = useLocation();
  const history = useHistory();
  const { teams, loading } = useTeams();

  useEffect(() => {
    if (!teamIdFromUrl && !loading && !selectedTeamId) {
      const defaultTeam = teams.find((team) => team.default);

      setSelectedTeamId(defaultTeam.id);
    }
  }, [teamIdFromUrl, teams, loading, selectedTeamId, setSelectedTeamId]);

  useEffect(() => {
    if (teamIdFromUrl) {
      setSelectedTeamId(teamIdFromUrl);
    }
  }, [teamIdFromUrl, setSelectedTeamId]);

  function setTeamId(teamId: string) {
    if (teamIdFromUrl) {
      // this means we are on a page that has :teamId in the url
      // so we need to update the url to the new teamId
      // the internal state will be updated via the useEffect above
      const teamPaths = [
        teamModelsPagePath,
        teamChainsPagePath,
        teamTrainingPagePath,
        teamModelApisPagePath,
        teamSettingsPagePath,
      ];

      // find which team path matches the current location
      const matchedPath = teamPaths.find((path) =>
        matchPath(location.pathname, { path, exact: true }),
      );

      if (matchedPath) {
        const newPath = generatePath(matchedPath, { teamId });
        history.push(newPath);
      }
    } else {
      // otherwise, we are on a page that does not have :teamId in the url
      // so we just need to update the internal state
      setSelectedTeamId(teamId);
    }
  }

  function setDefaultTeamId() {
    const defaultTeam = teams.find((team) => team.default);
    setTeamId(defaultTeam.id);
  }

  return { selectedTeamId, setTeamId, setDefaultTeamId, loading };
}

export { useWorkspaceSection, useTeam, useTeams, useSelectedTeamId };
